import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  getJobList,
  deleteDocument,
  renameDocument,
  removeTagFromDocument,
  getTag,
} from "../../utils/Api";
import { useNavigate, useSearchParams } from "react-router-dom";
import DocumentUpload from "../../modals/DocumentUpload";
import DeleteDocumentModal from "../../modals/DeleteDocumentModal";
import AddTagsToDocumentsModal from "../../modals/AddTagsToDocumentsModal";
import LoadingPage from "../../components/LoadingPage";
import { useAuth } from "../../utils/AuthContext";
import { useDocumentContext } from "../../contexts/DocumentContext";
import { useUpload } from "../../contexts/UploadContext";

import {
  Pencil,
  Trash2,
  ArrowUp,
  ArrowDown,
  Tag as TagIcon,
  Share2,
  Loader2,
  CheckCircle2,
} from "lucide-react";

import SecondaryButton from "../../components/Buttons/SecondaryButton";
import Tag from "../../components/Tag";

export type DocumentItem = Record<string, any>;

const PAGE_SIZE = 20;

const Documents: React.FC = () => {
  const { user } = useAuth();
  const { refreshSidebar } = useDocumentContext();
  const { uploads } = useUpload();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [documentList, setDocumentList] = useState<DocumentItem[]>([]);
  const [totalDocuments, setTotalDocuments] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddTagsModalOpen, setIsAddTagsModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<DocumentItem | null>(
    null,
  );
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingDocId, setEditingDocId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string>("");
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [selectedTag, setSelectedTag] = useState<{
    id: string;
    name: string;
    color: string;
  } | null>(null);

  const [justCompletedIds, setJustCompletedIds] = useState<Set<string>>(
    new Set(),
  );

  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevUploadsRef = useRef(uploads);

  const remainingCount = totalDocuments - documentList.length;

  const getDocumentUploadStatus = (docId: string): string | null => {
    const upload = uploads.find(
      (u) =>
        u.documentId === docId &&
        u.status !== "complete" &&
        u.status !== "error",
    );
    return upload ? upload.status : null;
  };

  const fetchDocuments = useCallback(
    async (offset: number, append: boolean, limitOverride?: number) => {
      try {
        if (offset === 0) setLoading(true);
        else setLoadingMore(true);

        const tagId = searchParams.get("tag");
        const params: Parameters<typeof getJobList>[0] = {
          limit: limitOverride ?? PAGE_SIZE,
          offset,
          sort_order: sortOrder,
        };
        if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
        if (tagId && tagId !== "uncategorized") params.tag_id = tagId;
        if (tagId === "uncategorized") params.untagged = true;

        const { data } = await getJobList(params);

        let docs: DocumentItem[] = [];
        if (data.documents && typeof data.documents === "object") {
          docs = Object.entries(data.documents).map(
            ([id, docData]: [string, any]) => ({
              ...docData,
              id,
            }),
          );
        }

        // Filter to personal & shared docs (exclude org docs user doesn't own)
        const personalAndSharedDocs = docs.filter(
          (doc) =>
            !doc.organisation_id ||
            doc.user_id === user?.uid ||
            doc.is_shared_with_me,
        );

        if (append) {
          setDocumentList((prev) => [...prev, ...personalAndSharedDocs]);
        } else {
          setDocumentList(personalAndSharedDocs);
        }
        setTotalDocuments(data.total_documents ?? 0);
        setError(null);
      } catch (err: any) {
        setError(err.message ?? "Error fetching documents");
        if (!append) setDocumentList([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [debouncedSearch, sortOrder, searchParams, user?.uid],
  );

  useEffect(() => {
    fetchDocuments(0, false);
  }, [fetchDocuments]);

  useEffect(() => {
    const prev = prevUploadsRef.current;
    const newlyCompleted = uploads.filter(
      (u) =>
        u.status === "complete" &&
        u.documentId &&
        prev.find((p) => p.id === u.id)?.status !== "complete",
    );
    if (newlyCompleted.length > 0) {
      fetchDocuments(0, false);
      setJustCompletedIds((prev) => {
        const next = new Set(prev);
        newlyCompleted.forEach((u) => next.add(u.documentId!));
        return next;
      });
    }
    prevUploadsRef.current = uploads;
  }, [uploads]);

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(value);
    }, 300);
  };

  useEffect(() => {
    let cancelled = false;

    const tagId = searchParams.get("tag");
    if (tagId) {
      if (tagId === "uncategorized") {
        setSelectedTag({
          id: "uncategorized",
          name: "Uncategorized",
          color: "#9CA3AF",
        });
      } else {
        getTag(tagId)
          .then((response) => {
            if (!cancelled) {
              setSelectedTag(response.data);
            }
          })
          .catch((error) => {
            if (!cancelled) {
              console.error("Failed to load tag:", error);
              setSelectedTag(null);
            }
          });
      }
    } else {
      setSelectedTag(null);
    }

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const handleShowMore = () => {
    fetchDocuments(documentList.length, true);
  };

  const refetch = () => {
    const currentCount = Math.max(documentList.length, PAGE_SIZE);
    fetchDocuments(0, false, currentCount);
  };

  const handleEditClick = (doc: DocumentItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingDocId(doc.id);
    setEditingName(doc.name);
  };

  const handleSaveEdit = async (docId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await renameDocument(docId, editingName);
      setEditingDocId(null);
      setEditingName("");
      refetch();
    } catch (err: any) {
      setError(err.message ?? "Error renaming document");
    }
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingDocId(null);
    setEditingName("");
  };

  const handleDeleteClick = (doc: DocumentItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setDocumentToDelete(doc);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      if (isBulkDelete) {
        await Promise.all(
          Array.from(selectedDocIds).map((id) => deleteDocument(id)),
        );
        setSelectedDocIds(new Set());
      } else if (documentToDelete) {
        await deleteDocument(documentToDelete.id);
        setDocumentToDelete(null);
      }
      setIsDeleteModalOpen(false);
      setIsBulkDelete(false);
      refetch();
    } catch (err: any) {
      setError(err.message ?? "Error deleting document(s)");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(
        documentList
          .filter((doc) => !doc.is_shared_with_me)
          .map((doc) => doc.id),
      );
      setSelectedDocIds(allIds);
    } else {
      setSelectedDocIds(new Set());
    }
  };

  const handleSelectDocument = (docId: string, checked: boolean) => {
    const newSelected = new Set(selectedDocIds);
    if (checked) {
      newSelected.add(docId);
    } else {
      newSelected.delete(docId);
    }
    setSelectedDocIds(newSelected);
  };

  const handleDeleteSelectedClick = () => {
    if (selectedDocIds.size === 0) return;
    setIsBulkDelete(true);
    setIsDeleteModalOpen(true);
  };

  const handleRemoveTag = async (tagId: string, documentId: string) => {
    try {
      await removeTagFromDocument(tagId, documentId);
      refetch();
      refreshSidebar();
    } catch (err: any) {
      setError(err.message ?? "Error removing tag from document");
    }
  };

  const selectableDocuments = documentList.filter(
    (doc) => !doc.is_shared_with_me,
  );
  const allDisplayedSelected =
    selectableDocuments.length > 0 &&
    selectableDocuments.every((doc) => selectedDocIds.has(doc.id));

  // Build placeholder rows for uploads that are active and not yet in the fetched list
  const activeUploadPlaceholders: DocumentItem[] = uploads
    .filter(
      (u) =>
        u.documentId &&
        u.status !== "complete" &&
        u.status !== "error" &&
        !documentList.some((doc) => doc.id === u.documentId),
    )
    .map((u) => ({
      id: u.documentId as string,
      name: u.documentName,
      page_count: 0,
      tags: [],
      created_at: null,
      _isUploadPlaceholder: true,
    }));

  const displayedDocuments = [...activeUploadPlaceholders, ...documentList];

  return (
    <div className="bg-gray-100 h-full flex flex-col overflow-hidden">
      <div className="w-full mx-auto pt-6 sm:pt-12 px-4 sm:px-6 lg:px-10 flex-shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <h3 className="text-2xl font-semibold">
              {selectedTag ? (
                <span className="flex items-center gap-2">
                  {selectedTag.name}
                </span>
              ) : (
                "All documents"
              )}
            </h3>
            <p className="text-gray-500">
              {selectedTag
                ? `Showing documents labeled with "${selectedTag.name}"`
                : "View, edit and analyse your documents"}
            </p>
          </div>

          {selectedDocIds.size > 0 && (
            <div className="flex rounded-full overflow-hidden border border-black bg-white h-10 w-fit">
              <button
                onClick={() => setIsAddTagsModalOpen(true)}
                disabled={isDeleting}
                className="flex items-center justify-center px-4 py-2 border-r border-r-black"
              >
                <TagIcon size={18} />
              </button>

              <button
                onClick={handleDeleteSelectedClick}
                disabled={isDeleting}
                className="flex items-center justify-center px-4 py-2"
              >
                <Trash2 size={18} />
              </button>
            </div>
          )}
        </div>

        <div className="mb-4 flex gap-3 items-center">
          <input
            type="text"
            className="flex-1 max-w-3xl px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#1d365d] focus:border-transparent"
            placeholder="Search document..."
            aria-label="Search documents"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>

        {error && (
          <div className="mb-4">
            <div
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded"
              role="alert"
            >
              {error}
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-10">
        {loading ? (
          <LoadingPage
            fullScreen={false}
            variant="light"
            message="Loading your documents"
          />
        ) : (
          <div className="bg-white rounded shadow overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white border-b border-gray-200">
                <tr>
                  <th className="px-3 sm:px-6 py-3 text-left text-gray-600 w-12">
                    <input
                      type="checkbox"
                      checked={allDisplayedSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="w-4 h-4 cursor-pointer"
                      aria-label="Select all documents"
                      disabled={documentList.length === 0}
                    />
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-gray-600">
                    Name
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-gray-600 hidden md:table-cell">
                    Labels
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-gray-600 hidden lg:table-cell">
                    Owner
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-gray-600 hidden sm:table-cell">
                    Pages
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-gray-600">
                    <button
                      onClick={toggleSortOrder}
                      className="flex items-center gap-2 text-gray-600 transition-colors"
                      title={`Sort by date ${
                        sortOrder === "asc" ? "descending" : "ascending"
                      }`}
                      disabled={documentList.length === 0}
                    >
                      Created at
                      {sortOrder === "desc" ? (
                        <ArrowDown size={16} />
                      ) : (
                        <ArrowUp size={16} />
                      )}
                    </button>
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-right text-gray-600">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {documentList.length === 0 &&
                activeUploadPlaceholders.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-3 sm:px-6 py-8 sm:py-12 text-center"
                    >
                      <div className="text-gray-500">
                        {searchQuery ? (
                          <>
                            <h4 className="text-lg font-semibold mb-2">
                              No documents found
                            </h4>
                            <p>
                              No documents match your search "{searchQuery}"
                            </p>
                          </>
                        ) : (
                          <>
                            <h4 className="text-lg font-semibold mb-2">
                              No documents
                            </h4>
                            <p>Create your first document to get started</p>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  displayedDocuments.map((doc) => {
                    const uploadStatus = getDocumentUploadStatus(doc.id);
                    const uploading = uploadStatus !== null;
                    return (
                      <tr
                        key={doc.id}
                        className={`transition-colors duration-150 ${
                          uploading
                            ? "bg-gray-50 opacity-60"
                            : "hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          {!doc.is_shared_with_me && (
                            <input
                              type="checkbox"
                              checked={selectedDocIds.has(doc.id)}
                              onChange={(e) =>
                                handleSelectDocument(doc.id, e.target.checked)
                              }
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 cursor-pointer disabled:cursor-not-allowed"
                              aria-label={`Select ${doc.name || "document"}`}
                              disabled={uploading}
                            />
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          {editingDocId === doc.id ? (
                            <input
                              type="text"
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#1d365d] focus:border-transparent"
                              autoFocus
                            />
                          ) : (
                            <div className="flex flex-col">
                              {doc.organisation_name && (
                                <span className="text-xs text-gray-500">
                                  {doc.organisation_name}
                                </span>
                              )}
                              <div className="flex items-center gap-2">
                                {uploading ? (
                                  <span className="text-gray-500 flex items-center gap-2">
                                    {doc.name || "Untitled Document"}
                                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium text-amber-700 bg-amber-100 rounded-full">
                                      <Loader2
                                        size={12}
                                        className="animate-spin"
                                      />
                                      {uploadStatus === "processing"
                                        ? "Processing..."
                                        : "Uploading..."}
                                    </span>
                                  </span>
                                ) : (
                                  <span
                                    className="text-[#1d365d] hover:text-[#2d4a75] cursor-pointer flex items-center gap-2"
                                    onClick={() => {
                                      setJustCompletedIds((prev) => {
                                        const next = new Set(prev);
                                        next.delete(doc.id);
                                        return next;
                                      });
                                      navigate(`/view/${doc.id}`);
                                    }}
                                  >
                                    {doc.name || "Untitled Document"}
                                    {justCompletedIds.has(doc.id) && (
                                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                                        <CheckCircle2 size={12} />
                                        Complete
                                      </span>
                                    )}
                                  </span>
                                )}
                                {doc.is_shared_with_me && (
                                  <span
                                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-blue-700 bg-blue-100 rounded-full"
                                    title="Shared with you"
                                  >
                                    <Share2 size={12} />
                                    Shared
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-4 hidden md:table-cell">
                          <div className="flex flex-wrap gap-1">
                            {doc.tags && doc.tags.length > 0 ? (
                              doc.tags.map((tag: any) => (
                                <Tag
                                  key={tag.id}
                                  id={tag.id}
                                  name={tag.name}
                                  color={tag.color}
                                  onRemove={(tagId) =>
                                    handleRemoveTag(tagId, doc.id)
                                  }
                                />
                              ))
                            ) : (
                              <span className="text-gray-400 text-sm">
                                Uncategorised
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-gray-700 hidden lg:table-cell">
                          {doc.owner_name || "Unknown"}
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-gray-700 hidden sm:table-cell">
                          {doc.page_count || 0}
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-gray-700 text-sm">
                          {doc.created_at
                            ? new Date(doc.created_at).toLocaleString("en-GB", {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: false,
                              })
                            : "Recently uploaded"}
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm">
                          {editingDocId === doc.id ? (
                            <div className="flex gap-2 justify-end">
                              <button
                                onClick={(e) => handleSaveEdit(doc.id, e)}
                                className="text-green-600 hover:text-green-800 font-medium"
                              >
                                Save
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="text-gray-600 hover:text-gray-800 font-medium"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex gap-3 justify-end">
                              {!doc.is_shared_with_me && !uploading && (
                                <>
                                  <button
                                    onClick={(e) => handleEditClick(doc, e)}
                                    className="text-[#1d365d] hover:text-[#2d4a75] transition-colors"
                                    title="Edit name"
                                  >
                                    <Pencil size={18} />
                                  </button>
                                  <button
                                    onClick={(e) => handleDeleteClick(doc, e)}
                                    className="text-red-600 hover:text-red-800 transition-colors"
                                    title="Delete document"
                                  >
                                    <Trash2 size={18} />
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* pagination controls */}
        {!loading && documentList.length > 0 && (
          <div className="mt-10 pb-12 text-center">
            {remainingCount > 0 && (
              <SecondaryButton
                onClick={handleShowMore}
                className="mb-3"
                disabled={loadingMore}
              >
                {loadingMore
                  ? "Loading..."
                  : `Show ${Math.min(remainingCount, PAGE_SIZE)} more document${Math.min(remainingCount, PAGE_SIZE) === 1 ? "" : "s"}`}
              </SecondaryButton>
            )}
            <p className="text-gray-600 text-center">
              Showing {documentList.length} out of {totalDocuments} documents
            </p>
          </div>
        )}
      </div>

      <DocumentUpload
        mode="create"
        isOpen={isUploadModalOpen}
        setIsOpen={setIsUploadModalOpen}
      />

      <DeleteDocumentModal
        document={documentToDelete}
        isDeleting={isDeleting}
        onConfirmDelete={confirmDelete}
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setIsBulkDelete(false);
        }}
        bulkDeleteCount={isBulkDelete ? selectedDocIds.size : undefined}
      />

      <AddTagsToDocumentsModal
        isOpen={isAddTagsModalOpen}
        setIsOpen={setIsAddTagsModalOpen}
        documentIds={Array.from(selectedDocIds)}
        onComplete={() => {
          setSelectedDocIds(new Set());
          refetch();
          refreshSidebar();
        }}
      />
    </div>
  );
};

export default Documents;
